import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  Ban,
  ExternalLink,
  Loader2,
  Network,
  Plus,
  RefreshCw,
  Save,
  Search,
  Shield,
  Sparkles,
  Trash2,
  TrendingUp,
  X,
  Zap,
} from 'lucide-react';
import { api, ApiError } from '../lib/api.js';
import { Badge, ConfirmModal, GlassButton, GlassCard, GradientText, PageHeader, ProviderIcon, toast } from '../ui/index.js';
import { SourceProviderPanel } from './SourceProviderPanel.js';
import { discoverPanel, readDiscoverAnswer, type DiscoveredModel } from './discover-answer.js';
import { cacheSavingOf, type CacheTier } from './cache-saving.js';
import { SpendCheckCard } from './SpendCheckCard.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ModelGatewaySecretView {
  secretId: string | null;
  secretName: string;
  configured: boolean;
  updatedAt: string | null;
  rotatedAt: string | null;
}

interface ModelGatewaySettings {
  enabled: boolean;
  failover: 'primary' | 'primary-then-backup';
  gatewayBaseUrl: string;
  defaultMarkupPercent: number;
  guardrailsEnabled: boolean;
  guardrailMode: 'off' | 'standard' | 'strict';
  guardrailInputScreening: boolean;
  guardrailOutputScreening: boolean;
  guardrailEngine: string;
  financePreflightEnabled: boolean;
  financeFailMode: 'fail-open' | 'fail-closed';
  financeTimeoutMs: number;
  financeCacheTtlMs: number;
  upstreamTimeoutMs: number;
  rolloutMode: 'all-orgs' | 'pilot' | 'disabled';
  pilotOrgIds: string[];
  blockedOrgIds: string[];
  source: 'settings-hub' | 'local';
  scopeOrder: string[];
  secrets: Record<'openrouterPrimaryKey' | 'openrouterBackupKey', ModelGatewaySecretView>;
}

interface ManagedGatewayModel {
  id: string;
  upstreamModelKey: string;
  upstreamProvider: string;
  displayName: string;
  exposed: boolean;
  visibleByDefault: boolean;
  upstreamInputCostPerMillion: number;
  upstreamCachedInputCostPerMillion: number;
  upstreamCacheWriteCostPerMillion: number;
  upstreamOutputCostPerMillion: number;
  markupMode: 'inherit-default' | 'override-percent' | 'fixed-sell-price';
  markupPercentOverride: number | null;
  saleInputPricePerMillion: number;
  saleCachedInputPricePerMillion: number;
  saleCacheWritePricePerMillion: number;
  saleOutputPricePerMillion: number;
  supportsCaching: boolean;
  contextWindow: number | null;
  maxOutputTokens: number | null;
  intentSource: 'markup' | 'fixed-price';
  guardrailEnabled: boolean;
  guardrailCostPerMillion: number;
  reasoningEffort: string | null;
  createdAt: string;
  updatedAt: string;
}

interface GatewayStatus {
  enabled: boolean;
  upstream: string;
  primaryKeyConfigured: boolean;
  backupKeyConfigured: boolean;
  catalogCount: number;
  exposedCount: number;
  guardrailMode: string;
  guardrailsEnabled: boolean;
  financePreflightEnabled: boolean;
  rolloutMode: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return 'Not configured';
  return new Date(value).toLocaleString();
}

function formatPrice(n: number): string {
  if (n === 0) return '$0.00';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

function computeMargin(upstream: number, sale: number): string {
  if (upstream <= 0) return '—';
  const margin = ((sale / upstream) - 1) * 100;
  return `${margin.toFixed(0)}%`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------


/** Said on hover over the Cache saving header. */
const CACHE_HEADER_HINT = 'How much cheaper a cached input token is than a fresh one. Higher is better.';

const CACHE_TIER_STYLE: Record<CacheTier, string> = {
  great: 'bg-emerald-400/10 text-emerald-300',
  fair: 'bg-amber-400/10 text-amber-300',
  poor: 'bg-red-400/10 text-red-300',
};

/** A model's cache saving: green where caching pays most, red where it barely pays. */
function CacheSavingChip({ model }: { model: { supportsCaching: boolean; upstreamInputCostPerMillion: number; upstreamCachedInputCostPerMillion: number } }) {
  if (!model.supportsCaching) return <span className="text-xs text-white/20">—</span>;
  const saving = cacheSavingOf(model);
  if (!saving) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2 py-0.5 text-xs text-white/50" title="Caches, but its cached price is not published.">
        <Zap className="h-3 w-3" /> ✓
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs ${CACHE_TIER_STYLE[saving.tier]}`}
      title={`Cached input costs ${formatPrice(model.upstreamCachedInputCostPerMillion)}/M against ${formatPrice(model.upstreamInputCostPerMillion)}/M fresh: ${saving.percent}% cheaper. Higher is better.`}
    >
      <Zap className="h-3 w-3" /> {saving.percent}% off
    </span>
  );
}

export function PlatformModelGatewayPage() {
  const [settings, setSettings] = useState<ModelGatewaySettings | null>(null);
  const [form, setForm] = useState<ModelGatewaySettings | null>(null);
  const [status, setStatus] = useState<GatewayStatus | null>(null);
  const [catalog, setCatalog] = useState<ManagedGatewayModel[]>([]);
  const [discovered, setDiscovered] = useState<DiscoveredModel[]>([]);
  // Why discovery failed, kept apart from `discovered` so a failure is never shown as "no models".
  const [discoverError, setDiscoverError] = useState<string | null>(null);
  const [discoverProblems, setDiscoverProblems] = useState<string[]>([]);
  const [discoverSkipped, setDiscoverSkipped] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [secretDrafts, setSecretDrafts] = useState<Record<string, string>>({
    openrouterPrimaryKey: '',
    openrouterBackupKey: '',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModels, setShowAddModels] = useState(false);

  // --- Markup edit slide-in panel state ---
  const [editingModel, setEditingModel] = useState<ManagedGatewayModel | null>(null);
  const [editMarkupMode, setEditMarkupMode] = useState<'inherit-default' | 'override-percent' | 'fixed-sell-price'>('inherit-default');
  const [editMarkupPercent, setEditMarkupPercent] = useState<number>(0);
  const [editSaleInput, setEditSaleInput] = useState<number>(0);
  const [editSaleOutput, setEditSaleOutput] = useState<number>(0);
  const [editGuardrailEnabled, setEditGuardrailEnabled] = useState<boolean>(true);
  const [editExposed, setEditExposed] = useState<boolean>(false);
  const [editVisibleByDefault, setEditVisibleByDefault] = useState<boolean>(false);
  const [editDisplayName, setEditDisplayName] = useState<string>('');
  const [editReasoningEffort, setEditReasoningEffort] = useState<string>('');
  const [savingModel, setSavingModel] = useState(false);
  const [cacheOnlyFilter, setCacheOnlyFilter] = useState<boolean>(false);

  // --- Add Model modal filter state ---
  const [filterSourceProvider, setFilterSourceProvider] = useState<string>('all');
  const [filterUpstreamProvider, setFilterUpstreamProvider] = useState<string>('all');
  const [filterMaxCost, setFilterMaxCost] = useState<string>('');
  const [filterMinCachePct, setFilterMinCachePct] = useState<string>('');
  const [filterMinContext, setFilterMinContext] = useState<string>('');

  // --- Cache strategy & param compat state (loaded when edit panel opens) ---
  const [cacheStrategy, setCacheStrategy] = useState<{
    upstreamModelKey: string;
    strategy: string;
    lastError: string | null;
    lastErrorAt: string | null;
    lastSuccessAt: string | null;
    lastCachedTokens: number | null;
    totalRequests: number;
    totalCacheHits: number;
    totalCacheWrites: number;
  } | null>(null);
  const [paramCompatRules, setParamCompatRules] = useState<Array<{
    id: number;
    upstreamModelKey: string;
    paramName: string;
    badValue: string | null;
    workaroundValue: string | null;
    action: string;
    errorPattern: string;
    lastError: string | null;
    lastErrorAt: string | null;
    lastSuccessAt: string | null;
    totalHits: number;
  }>>([]);
  const [loadingStrategy, setLoadingStrategy] = useState(false);
  const [editCacheStrategy, setEditCacheStrategy] = useState<string>('');
  const [savingStrategy, setSavingStrategy] = useState(false);

  function openEditPanel(model: ManagedGatewayModel) {
    setEditingModel(model);
    setEditMarkupMode(model.markupMode);
    setEditMarkupPercent(model.markupPercentOverride ?? 0);
    setEditSaleInput(model.saleInputPricePerMillion);
    setEditSaleOutput(model.saleOutputPricePerMillion);
    setEditGuardrailEnabled(model.guardrailEnabled);
    setEditExposed(model.exposed);
    setEditVisibleByDefault(model.visibleByDefault);
    setEditDisplayName(model.displayName);
    setEditReasoningEffort(model.reasoningEffort ?? '');
    // Load cache strategy and param compat rules for this model
    void loadStrategyForModel(model.upstreamModelKey);
  }

  async function loadStrategyForModel(upstreamModelKey: string) {
    setLoadingStrategy(true);
    try {
      const [strategies, rules] = await Promise.all([
        api.get<{ strategies: Array<{ upstreamModelKey: string; strategy: string; lastError: string | null; lastErrorAt: string | null; lastSuccessAt: string | null; lastCachedTokens: number | null; totalRequests: number; totalCacheHits: number; totalCacheWrites: number; }> }>('/v1/cache-strategies'),
        api.get<{ rules: Array<{ id: number; upstreamModelKey: string; paramName: string; badValue: string | null; workaroundValue: string | null; action: string; errorPattern: string; lastError: string | null; lastErrorAt: string | null; lastSuccessAt: string | null; totalHits: number; }> }>('/v1/param-compat'),
      ]);
      const found = strategies.strategies?.find(s => s.upstreamModelKey === upstreamModelKey) ?? null;
      setCacheStrategy(found);
      setEditCacheStrategy(found?.strategy ?? 'default');
      setParamCompatRules(rules.rules?.filter(r => r.upstreamModelKey === upstreamModelKey) ?? []);
    } catch {
      setCacheStrategy(null);
      setEditCacheStrategy('default');
      setParamCompatRules([]);
    } finally {
      setLoadingStrategy(false);
    }
  }

  async function saveCacheStrategy(upstreamModelKey: string, strategy: string) {
    setSavingStrategy(true);
    try {
      await api.put(`/v1/cache-strategies/${encodeURIComponent(upstreamModelKey)}`, { strategy });
      toast.success(strategy === 'default'
        ? `Cache strategy override cleared for ${upstreamModelKey} — using default detection.`
        : `Cache strategy set to '${strategy}' for ${upstreamModelKey}.`);
      void loadStrategyForModel(upstreamModelKey);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to set cache strategy.';
      toast.error(message);
    } finally {
      setSavingStrategy(false);
    }
  }

  async function saveStrategyOverride(upstreamModelKey: string) {
    await saveCacheStrategy(upstreamModelKey, 'default');
  }

  async function resetParamCompat(upstreamModelKey: string) {
    try {
      await api.delete(`/v1/param-compat/${encodeURIComponent(upstreamModelKey)}`);
      toast.success(`Parameter compatibility rules reset for ${upstreamModelKey}.`);
      void loadStrategyForModel(upstreamModelKey);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to reset param compat rules.';
      toast.error(message);
    }
  }

  async function saveModelEdit() {
    if (!editingModel) return;
    try {
      setSavingModel(true);
      await api.post('/api/v1/platform/model-gateway/catalog/upsert', {
        ...editingModel,
        displayName: editDisplayName,
        exposed: editExposed,
        visibleByDefault: editVisibleByDefault,
        markupMode: editMarkupMode,
        markupPercentOverride: editMarkupMode === 'override-percent' ? editMarkupPercent : null,
        saleInputPricePerMillion: editMarkupMode === 'fixed-sell-price' ? editSaleInput : undefined,
        saleOutputPricePerMillion: editMarkupMode === 'fixed-sell-price' ? editSaleOutput : undefined,
        guardrailEnabled: editGuardrailEnabled,
        reasoningEffort: editReasoningEffort || undefined,
      });
      toast.success(`Model "${editDisplayName}" updated.`);
      setEditingModel(null);
      void loadCatalog();
      void loadStatus();
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to update model.';
      toast.error(message);
    } finally {
      setSavingModel(false);
    }
  }

  // --- Org rollout slide-in panel state ---
  type SlidePanel = 'whitelist' | 'blacklist' | 'orgPicker' | 'sourceProvider' | 'addModel' | 'pricing' | 'gatewayControls' | null;
  const [slidePanel, setSlidePanel] = useState<SlidePanel>(null);
  const [slideContext, setSlideContext] = useState<'whitelist' | 'blacklist'>('whitelist');
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const [orgSearchResults, setOrgSearchResults] = useState<Array<{ orgId: string; name: string; slug: string; memberCount: number }>>([]);

  // --- Source Provider state ---
  interface SourceProviderView {
    id: string; displayName: string; apiFormat: string; baseUrl: string;
    authHeader: string; authPrefix: string; extraHeaders: Record<string,string> | null;
    enabled: boolean; supportsCaching: boolean; supportsReasoningEffort: boolean;
    supportsByok: boolean; timeoutMs: number; primarySecretId: string | null;
    backupSecretId: string | null; createdAt: string; updatedAt: string;
    primarySecret: { secretId: string | null; secretName: string; configured: boolean; updatedAt: string | null };
    backupSecret: { secretId: string | null; secretName: string; configured: boolean; updatedAt: string | null };
  }
  const [sourceProviders, setSourceProviders] = useState<SourceProviderView[]>([]);
  const [providerBalances, setProviderBalances] = useState<Array<{
    providerId: string; displayName: string; topUpUrl: string;
    balanceCheckSupported: boolean; balance: number | null;
    currency: string | null; isAvailable: boolean | null; error: string | null;
  }>>([]);
  const [loadingBalances, setLoadingBalances] = useState(false);

  // --- Load source providers ---
  async function loadSourceProviders() {
    try {
      const res = await api.get<{ providers: SourceProviderView[] }>('/api/v1/platform/source-providers');
      setSourceProviders(res.providers || []);
    } catch {}
  }

  async function loadBalances() {
    try {
      setLoadingBalances(true);
      const res = await api.get<{ balances: Array<{
        providerId: string; displayName: string; topUpUrl: string;
        balanceCheckSupported: boolean; balance: number | null;
        currency: string | null; isAvailable: boolean | null; error: string | null;
      }> }>('/api/v1/platform/source-providers/balances');
      setProviderBalances(res.balances || []);
    } catch {
      // Non-fatal
    } finally {
      setLoadingBalances(false);
    }
  }


  const [orgSearching, setOrgSearching] = useState(false);
  const [orgNamesCache, setOrgNamesCache] = useState<Record<string, { name: string; slug: string }>>({});

  // --- Org lookup ---
  async function searchOrgs(query: string) {
    if (!query.trim()) {
      setOrgSearchResults([]);
      return;
    }
    try {
      setOrgSearching(true);
      const result = await api.get<{ orgs: Array<{ orgId: string; name: string; slug: string; memberCount: number }>; total: number }>(`/api/v1/platform/orgs?search=${encodeURIComponent(query)}&pageSize=50`);
      setOrgSearchResults(result.orgs);
      // Cache names for display
      const cache = { ...orgNamesCache };
      for (const org of result.orgs) {
        cache[org.orgId] = { name: org.name, slug: org.slug };
      }
      setOrgNamesCache(cache);
    } catch {
      setOrgSearchResults([]);
    } finally {
      setOrgSearching(false);
    }
  }

  async function loadOrgNames(orgIds: string[]): Promise<void> {
    const missing = orgIds.filter((id) => !orgNamesCache[id]);
    if (missing.length === 0) return;
    // Try to resolve names via search (one per org is not ideal, but the orgs API doesn't have a batch endpoint)
    const cache = { ...orgNamesCache };
    for (const id of missing) {
      try {
        const result = await api.get<{ orgs: Array<{ orgId: string; name: string; slug: string }> }>(`/api/v1/platform/orgs?search=${encodeURIComponent(id)}&pageSize=5`);
        const match = result.orgs.find((o) => o.orgId === id);
        if (match) {
          cache[id] = { name: match.name, slug: match.slug };
        } else {
          cache[id] = { name: id.slice(0, 8) + '...', slug: '' };
        }
      } catch {
        cache[id] = { name: id.slice(0, 8) + '...', slug: '' };
      }
    }
    setOrgNamesCache(cache);
  }

  function openOrgPicker(context: 'whitelist' | 'blacklist') {
    setSlideContext(context);
    setSlidePanel('orgPicker');
    setOrgSearchQuery('');
    setOrgSearchResults([]);
  }

  function addOrgToList(orgId: string) {
    if (!form) return;
    if (slideContext === 'whitelist') {
      if (!form.pilotOrgIds.includes(orgId)) {
        setForm((c) => c ? { ...c, pilotOrgIds: [...c.pilotOrgIds, orgId] } : c);
      }
    } else {
      if (!form.blockedOrgIds.includes(orgId)) {
        setForm((c) => c ? { ...c, blockedOrgIds: [...c.blockedOrgIds, orgId] } : c);
      }
    }
  }

  function removeOrgFromList(orgId: string, list: 'whitelist' | 'blacklist') {
    if (!form) return;
    if (list === 'whitelist') {
      setForm((c) => c ? { ...c, pilotOrgIds: c.pilotOrgIds.filter((id) => id !== orgId) } : c);
    } else {
      setForm((c) => c ? { ...c, blockedOrgIds: c.blockedOrgIds.filter((id) => id !== orgId) } : c);
    }
  }

  // --- Loaders ---

  async function loadSettings() {
    try {
      const result = await api.get<ModelGatewaySettings>('/api/v1/platform/settings/model-gateway');
      setSettings(result);
      setForm(result);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to load model gateway settings.';
      toast.error(message);
    }
  }

  async function loadStatus() {
    try {
      const result = await api.get<GatewayStatus>('/api/v1/platform/model-gateway/status');
      setStatus(result);
    } catch { /* ignore */ }
  }

  async function loadCatalog() {
    try {
      const result = await api.get<{ models: ManagedGatewayModel[] }>('/api/v1/platform/model-gateway/catalog');
      setCatalog(result.models);
    } catch { /* ignore */ }
  }

  async function loadAll() {
    setLoading(true);
    await Promise.all([loadSettings(), loadStatus(), loadCatalog(), loadSourceProviders(), loadBalances()]);
    setLoading(false);
  }

  useEffect(() => { void loadAll(); }, []);

  // Auto-run discovery when Add Model modal opens (if not already loaded)
  useEffect(() => {
    if (showAddModels && discovered.length === 0 && !syncing) {
      void syncCatalog();
    }
  }, [showAddModels]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load org names when settings change
  useEffect(() => {
    if (form) {
      void loadOrgNames([...form.pilotOrgIds, ...form.blockedOrgIds]);
    }
  }, [form?.pilotOrgIds, form?.blockedOrgIds]);

  // --- Actions ---

  async function saveSettings() {
    if (!form) return;
    try {
      setSaving(true);
      const payload: Record<string, unknown> = {
        enabled: form.enabled,
        failover: form.failover,
        gatewayBaseUrl: form.gatewayBaseUrl,
        defaultMarkupPercent: form.defaultMarkupPercent,
        guardrailsEnabled: form.guardrailsEnabled,
        guardrailMode: form.guardrailMode,
        guardrailInputScreening: form.guardrailInputScreening,
        guardrailOutputScreening: form.guardrailOutputScreening,
        guardrailEngine: form.guardrailEngine,
        financePreflightEnabled: form.financePreflightEnabled,
        financeFailMode: form.financeFailMode,
        financeTimeoutMs: form.financeTimeoutMs,
        financeCacheTtlMs: form.financeCacheTtlMs,
        upstreamTimeoutMs: form.upstreamTimeoutMs,
        rolloutMode: form.rolloutMode,
        pilotOrgIds: form.pilotOrgIds,
      };
      if (secretDrafts.openrouterPrimaryKey.trim()) payload['openrouterPrimaryKey'] = secretDrafts.openrouterPrimaryKey.trim();
      if (secretDrafts.openrouterBackupKey.trim()) payload['openrouterBackupKey'] = secretDrafts.openrouterBackupKey.trim();

      const saved = await api.put<ModelGatewaySettings>('/api/v1/platform/settings/model-gateway', payload);
      setSettings(saved);
      setForm(saved);
      setSecretDrafts({ openrouterPrimaryKey: '', openrouterBackupKey: '' });
      toast.success('Model gateway settings saved.');
      void loadStatus();
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to save settings.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }


  async function testConnection() {
    try {
      setTesting(true);
      // Send the draft primary key if entered, so the user can test before saving.
      const draftKey = secretDrafts.openrouterPrimaryKey.trim();
      const result = await api.post<{ success: boolean; modelCount?: number; error?: string; message?: string }>('/api/v1/platform/model-gateway/openrouter/test', draftKey ? { apiKey: draftKey } : {});
      if (result.success) {
        toast.success(result.message ?? `Connection successful — ${result.modelCount} models available.`);
      } else {
        toast.error(result.error ?? 'Connection failed.');
      }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Test failed.';
      toast.error(message);
    } finally {
      setTesting(false);
    }
  }

  // A failure clears the list and is recorded in discoverError, which the dialog
  // checks before it checks for an empty list (discover-answer.ts says why).
  async function syncCatalog() {
    setSyncing(true);
    setDiscoverError(null);
    try {
      const reading = readDiscoverAnswer(await api.post<unknown>('/api/v1/platform/model-gateway/discover', {}));
      if (!reading.ok) throw new Error(reading.reason);
      setDiscovered(reading.models);
      setDiscoverProblems(reading.problems);
      setDiscoverSkipped(reading.skipped);
      if (reading.models.length > 0) toast.success(`Discovered ${reading.models.length} models from all configured providers.`);
    } catch (error) {
      setDiscovered([]);
      setDiscoverProblems([]);
      setDiscoverSkipped([]);
      setDiscoverError(error instanceof Error ? error.message : String(error));
    } finally {
      setSyncing(false);
    }
  }

  async function addModel(model: DiscoveredModel) {
    try {
      await api.post('/api/v1/platform/model-gateway/catalog/upsert', {
        id: model.upstreamModelKey,
        upstreamModelKey: model.upstreamModelKey,
        upstreamProvider: model.upstreamProvider,
        sourceProviderId: model.sourceProviderId,
        displayName: model.displayName,
        exposed: true,
        visibleByDefault: true,
        upstreamInputCostPerMillion: model.upstreamInputCostPerMillion,
        upstreamCachedInputCostPerMillion: model.upstreamCachedInputCostPerMillion,
        upstreamCacheWriteCostPerMillion: model.upstreamCacheWriteCostPerMillion,
        upstreamOutputCostPerMillion: model.upstreamOutputCostPerMillion,
        supportsCaching: model.supportsCaching,
        contextWindow: model.contextWindow,
        maxOutputTokens: model.maxOutputTokens,
        markupMode: 'inherit-default',
      });
      toast.success(`Added "${model.displayName}" to the catalog.`);
      void loadCatalog();
      void loadStatus();
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to add model.';
      toast.error(message);
    }
  }

  async function updateModel(id: string, patch: Partial<ManagedGatewayModel>) {
    try {
      const existing = catalog.find((m) => m.id === id);
      if (!existing) return;
      await api.post('/api/v1/platform/model-gateway/catalog/upsert', { ...existing, ...patch });
      void loadCatalog();
      void loadStatus();
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to update model.';
      toast.error(message);
    }
  }

  async function deleteModel(id: string) {
    try {
      await api.post('/api/v1/platform/model-gateway/catalog/delete', { id });
      toast.success('Model removed from catalog.');
      void loadCatalog();
      void loadStatus();
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to delete model.';
      toast.error(message);
    }
  }

  // --- Dirty checking ---

  /**
   * Returns true if the form has unsaved changes compared to the last saved settings.
   * Used to guard recompute/resolve actions that operate on saved values.
   */
  function hasUnsavedChanges(): boolean {
    if (!form || !settings) return false;
    const formSnapshot = JSON.stringify({
      enabled: form.enabled,
      failover: form.failover,
      gatewayBaseUrl: form.gatewayBaseUrl,
      defaultMarkupPercent: form.defaultMarkupPercent,
      guardrailsEnabled: form.guardrailsEnabled,
      guardrailMode: form.guardrailMode,
      guardrailInputScreening: form.guardrailInputScreening,
      guardrailOutputScreening: form.guardrailOutputScreening,
      guardrailEngine: form.guardrailEngine,
      financePreflightEnabled: form.financePreflightEnabled,
      financeFailMode: form.financeFailMode,
      financeTimeoutMs: form.financeTimeoutMs,
      financeCacheTtlMs: form.financeCacheTtlMs,
      upstreamTimeoutMs: form.upstreamTimeoutMs,
      rolloutMode: form.rolloutMode,
      pilotOrgIds: form.pilotOrgIds,
      blockedOrgIds: form.blockedOrgIds,
    });
    const savedSnapshot = JSON.stringify({
      enabled: settings.enabled,
      failover: settings.failover,
      gatewayBaseUrl: settings.gatewayBaseUrl,
      defaultMarkupPercent: settings.defaultMarkupPercent,
      guardrailsEnabled: settings.guardrailsEnabled,
      guardrailMode: settings.guardrailMode,
      guardrailInputScreening: settings.guardrailInputScreening,
      guardrailOutputScreening: settings.guardrailOutputScreening,
      guardrailEngine: settings.guardrailEngine,
      financePreflightEnabled: settings.financePreflightEnabled,
      financeFailMode: settings.financeFailMode,
      financeTimeoutMs: settings.financeTimeoutMs,
      financeCacheTtlMs: settings.financeCacheTtlMs,
      upstreamTimeoutMs: settings.upstreamTimeoutMs ?? 120000,
      rolloutMode: settings.rolloutMode,
      pilotOrgIds: settings.pilotOrgIds,
      blockedOrgIds: settings.blockedOrgIds,
    });
    return formSnapshot !== savedSnapshot;
  }

  // --- Save-before-action modal ---

  const [savePrompt, setSavePrompt] = useState<{ actionLabel: string; resolve: (ok: boolean) => void } | null>(null);
  const [savePromptSaving, setSavePromptSaving] = useState(false);

  /**
   * If there are unsaved changes, show a custom modal asking the user to save
   * first. Returns true if the caller should proceed (either no changes, or
   * user confirmed and the save succeeded). Returns false if the user declined
   * or the save failed.
   */
  async function ensureSavedBeforeAction(actionLabel: string): Promise<boolean> {
    if (!hasUnsavedChanges()) return true;
    return new Promise<boolean>((resolve) => {
      setSavePrompt({ actionLabel, resolve });
    });
  }

  async function handleSavePromptConfirm() {
    if (!savePrompt) return;
    setSavePromptSaving(true);
    try {
      await saveSettings();
    } finally {
      setSavePromptSaving(false);
    }
    const ok = !hasUnsavedChanges();
    savePrompt.resolve(ok);
    setSavePrompt(null);
  }

  function handleSavePromptCancel() {
    if (!savePrompt) return;
    savePrompt.resolve(false);
    setSavePrompt(null);
  }

  async function recomputePrices() {
    try {
      const ok = await ensureSavedBeforeAction('Recompute Prices');
      if (!ok) {
        toast.info('Recompute cancelled — save your changes first.');
        return;
      }
      // 1. Resolve guardrail pricing
      const guardrailResult = await api.post<{ success: boolean; inputCostPerMillion?: number; error?: string }>('/api/v1/platform/model-gateway/guardrail/resolve-pricing', {});
      if (guardrailResult.success && guardrailResult.inputCostPerMillion !== undefined) {
        const guardrailCost = guardrailResult.inputCostPerMillion;
        for (const model of catalog) {
          if (model.guardrailEnabled) {
            await api.post('/api/v1/platform/model-gateway/catalog/upsert', { ...model, guardrailCostPerMillion: guardrailCost });
          }
        }
      }
      // 2. Recompute sale prices
      await api.post('/api/v1/platform/model-gateway/catalog/recompute', {});
      // 3. Sync pricing table
      await api.post('/api/v1/platform/model-gateway/sync-pricing', {});
      toast.success('Prices recomputed, guardrail pricing resolved, and pricing table synced.');
      void loadCatalog();
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Recompute failed.';
      toast.error(message);
    }
  }

  async function syncPricingTable() {
    try {
      const ok = await ensureSavedBeforeAction('Sync Pricing Table');
      if (!ok) {
        toast.info('Sync cancelled — save your changes first.');
        return;
      }
      await api.post('/api/v1/platform/model-gateway/sync-pricing', {});
      toast.success('Pricing table synced with Model Gateway catalog. Exposed models added/updated; removed models with no activity pruned.');
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Sync failed.';
      toast.error(message);
    }
  }

  // AD-20260714-07: Update all model pricing from their provider (OpenRouter).
  // Fetches the latest upstream costs from the provider, then recomputes sale prices.
  async function updateAllPricingFromProviders() {
    try {
      const ok = await ensureSavedBeforeAction('Update All Pricing');
      if (!ok) {
        toast.info('Update cancelled — save your changes first.');
        return;
      }
      setSyncing(true);
      // 1. Discover from all providers (OpenRouter included), and bring the catalog's
      //    upstream keys and costs up to date from what they list. The router refreshes
      //    the catalog only when asked, so opening Add Models does not reprice anything.
      //    This replaced a call to /openrouter/sync, which the router has answered with
      //    a no-op since it was split from cockpit-server.
      const discovery = readDiscoverAnswer(await api.post<unknown>('/api/v1/platform/model-gateway/discover', { refreshCatalog: true }));
      if (!discovery.ok) throw new Error(discovery.reason);
      for (const problem of discovery.problems) toast.error(problem);
      // 2. Resolve guardrail pricing. Upserting writes every field, so it starts from the
      //    catalog as step 1 left it, not the copy this page loaded before it: the old copy
      //    would write the costs step 1 just replaced back over them.
      const guardrailResult = await api.post<{ success: boolean; inputCostPerMillion?: number; error?: string }>('/api/v1/platform/model-gateway/guardrail/resolve-pricing', {});
      if (guardrailResult.success && guardrailResult.inputCostPerMillion !== undefined) {
        const guardrailCost = guardrailResult.inputCostPerMillion;
        const refreshed = await api.get<{ models: ManagedGatewayModel[] }>('/api/v1/platform/model-gateway/catalog');
        for (const model of refreshed.models) {
          if (model.guardrailEnabled) {
            await api.post('/api/v1/platform/model-gateway/catalog/upsert', { ...model, guardrailCostPerMillion: guardrailCost });
          }
        }
      }
      // 3. Recompute sale prices from the refreshed upstream costs
      await api.post('/api/v1/platform/model-gateway/catalog/recompute', {});
      // 4. Sync pricing table
      await api.post('/api/v1/platform/model-gateway/sync-pricing', {});
      toast.success(discovery.problems.length > 0
        ? 'Pricing updated from the providers that answered, sale prices recomputed, and pricing table synced.'
        : 'All model pricing updated from providers, sale prices recomputed, and pricing table synced.');
      void loadCatalog();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Update failed.';
      toast.error(message);
    } finally {
      setSyncing(false);
    }
  }

  async function resolveGuardrailPricing() {
    try {
      const ok = await ensureSavedBeforeAction('Resolve Guardrail Pricing');
      if (!ok) {
        toast.info('Resolve cancelled — save your changes first.');
        return;
      }
      // Fetch the guardrail model pricing from OpenRouter
      const result = await api.post<{ success: boolean; inputCostPerMillion?: number; model?: string; error?: string }>('/api/v1/platform/model-gateway/guardrail/resolve-pricing', {});
      if (!result.success || result.inputCostPerMillion === undefined) {
        toast.error(result.error ?? 'Failed to resolve guardrail pricing.');
        return;
      }
      // Apply the guardrail cost to all models with guardrail enabled
      const guardrailCost = result.inputCostPerMillion;
      for (const model of catalog) {
        if (model.guardrailEnabled) {
          await api.post('/api/v1/platform/model-gateway/catalog/upsert', {
            ...model,
            guardrailCostPerMillion: guardrailCost,
          });
        }
      }
      // Recompute sale prices to include the new guardrail cost
      await api.post('/api/v1/platform/model-gateway/catalog/recompute', {});
      toast.success(`Guardrail pricing resolved: $${guardrailCost.toFixed(4)}/M input. Applied to ${catalog.filter(m => m.guardrailEnabled).length} models and sale prices recomputed.`);
      void loadCatalog();
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to resolve guardrail pricing.';
      toast.error(message);
    }
  }

  // --- Derived ---

  const filteredDiscovered = useMemo(() => {
    let result = discovered;
    if (cacheOnlyFilter) {
      result = result.filter((m) => m.supportsCaching);
    }
    if (filterSourceProvider !== 'all') {
      result = result.filter((m) => m.sourceProviderId === filterSourceProvider);
    }
    if (filterUpstreamProvider !== 'all') {
      result = result.filter((m) => m.upstreamProvider === filterUpstreamProvider);
    }
    if (filterMaxCost.trim()) {
      const maxCost = Number(filterMaxCost);
      if (!isNaN(maxCost)) {
        result = result.filter((m) => m.upstreamInputCostPerMillion <= maxCost);
      }
    }
    if (filterMinCachePct.trim()) {
      const minPct = Number(filterMinCachePct);
      if (!isNaN(minPct)) {
        result = result.filter((m) => {
          const saving = m.supportsCaching ? cacheSavingOf(m) : null;
          return saving !== null && saving.percent >= minPct;
        });
      }
    }
    if (filterMinContext.trim()) {
      const minCtx = Number(filterMinContext);
      if (!isNaN(minCtx)) {
        result = result.filter((m) => m.contextWindow !== null && m.contextWindow >= minCtx);
      }
    }
    if (!searchQuery.trim()) return result;
    const q = searchQuery.toLowerCase();
    return result.filter((m) =>
      m.id.toLowerCase().includes(q) ||
      m.displayName.toLowerCase().includes(q) ||
      m.upstreamProvider.toLowerCase().includes(q)
    );
  }, [discovered, searchQuery, cacheOnlyFilter, filterSourceProvider, filterUpstreamProvider, filterMaxCost, filterMinCachePct, filterMinContext]);

  // Unique source providers and upstream providers for filter dropdowns
  const sourceProviderOptions = useMemo(() => {
    const set = new Map<string, string>();
    for (const m of discovered) {
      if (m.sourceProviderId) set.set(m.sourceProviderId, m.sourceProviderId);
    }
    return Array.from(set.entries()).map(([id, label]) => ({ id, label }));
  }, [discovered]);

  const upstreamProviderOptions = useMemo(() => {
    const set = new Set<string>();
    for (const m of discovered) {
      if (m.upstreamProvider) set.add(m.upstreamProvider);
    }
    return Array.from(set).sort();
  }, [discovered]);

  const catalogIds = useMemo(() => new Set(catalog.map((m) => m.id)), [catalog]);

  const addModelsPanel = discoverPanel({ syncing, error: discoverError, count: discovered.length });

  // --- Render ---

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  if (!form || !settings) {
    return null;
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={<GradientText>Managed Model Gateway</GradientText>}
        subtitle="Manage AI models available to your users. Configure providers, pricing, and access controls."
        action={(
          <div className="flex items-center gap-2">
            <GlassButton variant="secondary" icon={<RefreshCw className="h-4 w-4" />} onClick={() => void loadAll()} loading={loading}>
              Refresh
            </GlassButton>
          </div>
        )}
      />

      {/* Status Strip */}
      {status && (
        <GlassCard>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Network className="h-4 w-4 text-indigo-300" />
              <span className="text-sm font-semibold text-white/85">Gateway:</span>
              <Badge variant={status.enabled ? 'success' : 'warning'}>{status.enabled ? 'Enabled' : 'Disabled'}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-white/85">Catalog:</span>
              <Badge variant="default">{status.exposedCount} exposed / {status.catalogCount} total</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-white/85">Rollout:</span>
              <Badge variant={status.rolloutMode === 'all-orgs' ? 'success' : status.rolloutMode === 'pilot' ? 'warning' : 'default'}>
                {status.rolloutMode}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-white/85">Markup:</span>
              <Badge variant="default">{form.defaultMarkupPercent}%</Badge>
            </div>
          </div>
        </GlassCard>
      )}

      {/* The spend checks platform admins ran, and spikes in the last 24 hours (plan 15, 4.10). */}
      <SpendCheckCard />

      {/* Provider Balance Cards */}
      {providerBalances.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {providerBalances.map((pb) => (
            <div key={pb.providerId} className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
              <ProviderIcon providerId={pb.providerId} className="h-3.5 w-3.5 shrink-0 text-sky-300" />
              <span className="text-xs font-medium text-white/70">{pb.displayName}</span>
              {pb.balanceCheckSupported ? (
                pb.error ? (
                  <span className="text-xs text-red-400/70">{pb.error}</span>
                ) : pb.balance !== null ? (
                  <span className="text-xs font-semibold text-white/90">
                    {pb.currency === 'CNY' ? '¥' : '$'}{pb.balance.toFixed(2)}
                  </span>
                ) : (
                  <span className="text-xs text-white/30">—</span>
                )
              ) : (
                <span className="text-xs text-white/30">N/A</span>
              )}
              {pb.isAvailable === false && (
                <span className="text-xs text-red-400/70">●</span>
              )}
              {pb.topUpUrl && (
                <a
                  href={pb.topUpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 text-xs text-sky-400 hover:text-sky-300 transition"
                >
                  Top Up <ExternalLink className="h-2.5 w-2.5" />
                </a>
              )}
            </div>
          ))}
          {loadingBalances && <Loader2 className="h-3.5 w-3.5 text-white/30 animate-spin self-center" />}
        </div>
      )}

      {/* Action Bar */}
      <div className="flex flex-wrap items-center gap-2">
        <GlassButton variant="secondary" icon={<Network className="h-4 w-4" />} onClick={() => setSlidePanel('sourceProvider')}>
          Source Providers
        </GlassButton>
        <GlassButton variant="secondary" icon={<Plus className="h-4 w-4" />} onClick={() => setShowAddModels(true)}>
          Add Model
        </GlassButton>
        <GlassButton variant="secondary" icon={<TrendingUp className="h-4 w-4" />} onClick={() => setSlidePanel('pricing')}>
          Pricing
        </GlassButton>
        <GlassButton variant="secondary" icon={<Shield className="h-4 w-4" />} onClick={() => setSlidePanel('gatewayControls')}>
          Gateway Controls
        </GlassButton>
      </div>

      {/* Catalog & Model Exposure — the main content */}
      <GlassCard className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-300" />
            <h3 className="text-sm font-semibold text-white">Model Catalog</h3>
          </div>
        </div>

        {catalog.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-white/40">
                  <th className="pb-2 pr-4">Model</th>
                  <th className="pb-2 pr-4">Source</th>
                  <th className="pb-2 pr-4 text-center" title={CACHE_HEADER_HINT}>Cache saving</th>
                  <th className="pb-2 pr-4 text-right">Upstream Cost</th>
                  <th className="pb-2 pr-4 text-right">Sale Price</th>
                  <th className="pb-2 pr-4 text-right">Margin</th>
                  <th className="pb-2 pr-4">Markup</th>
                  <th className="pb-2 pr-4">Exposed</th>
                  <th className="pb-2"></th>
                </tr>
              </thead>
              <tbody>
                {catalog.map((model) => (
                  <tr key={model.id} className="border-b border-white/5 cursor-pointer hover:bg-white/[0.02] transition group" onClick={() => openEditPanel(model)}>
                    <td className="py-2 pr-4">
                      <div className="font-medium text-white/85">{model.displayName}</div>
                      <div className="text-xs text-white/40">{model.id}</div>
                    </td>
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-1.5 text-white/60">
                        <ProviderIcon providerId={(model as any).sourceProviderId || model.upstreamProvider || 'openrouter'} className="h-3.5 w-3.5 shrink-0" />
                        <span>{(model as any).sourceProviderId || model.upstreamProvider || 'openrouter'}</span>
                      </div>
                    </td>
                    <td className="py-2 pr-4 text-center"><CacheSavingChip model={model} /></td>
                    <td className="py-2 pr-4 text-right text-white/60">
                      <div>{formatPrice(model.upstreamInputCostPerMillion)}/M in</div>
                      <div className="text-xs">{formatPrice(model.upstreamOutputCostPerMillion)}/M out</div>
                    </td>
                    <td className="py-2 pr-4 text-right text-emerald-300">
                      <div>{formatPrice(model.saleInputPricePerMillion)}/M in</div>
                      <div className="text-xs">{formatPrice(model.saleOutputPricePerMillion)}/M out</div>
                    </td>
                    <td className="py-2 pr-4 text-right text-white/70">
                      {computeMargin(model.upstreamInputCostPerMillion + (model.guardrailEnabled ? model.guardrailCostPerMillion : 0), model.saleInputPricePerMillion)}
                    </td>
                    <td className="py-2 pr-4">
                      <Badge variant={model.markupMode === 'inherit-default' ? 'default' : 'success'}>
                        {model.markupMode === 'inherit-default'
                          ? `Default (${form?.defaultMarkupPercent ?? 0}%)`
                          : model.markupMode === 'override-percent'
                            ? `${model.markupPercentOverride ?? 0}%`
                            : 'Fixed'}
                      </Badge>
                    </td>
                    <td className="py-2 pr-4" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={model.exposed}
                        onChange={(e) => void updateModel(model.id, { exposed: e.target.checked })}
                        className="h-4 w-4 rounded border-white/20 bg-white/10 text-emerald-400"
                      />
                    </td>
                    <td className="py-2" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => void deleteModel(model.id)} className="text-red-400/60 hover:text-red-400 opacity-0 group-hover:opacity-100 transition">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-lg border border-white/10 bg-white/[0.02] px-4 py-8 text-center text-sm text-white/40">
            No models in the catalog yet. Click "Add Model" to discover and add models from your configured source providers.
          </div>
        )}
      </GlassCard>

      {/* Add Model full-screen modal */}
      {showAddModels && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowAddModels(false)} />
          <div className="relative w-full max-w-5xl h-[85vh] bg-[rgba(20,20,35,0.98)] rounded-2xl border border-white/10 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <h3 className="text-sm font-semibold text-white">Add Models</h3>
              <div className="flex items-center gap-2">
                <GlassButton variant="secondary" icon={<RefreshCw className="h-4 w-4" />} onClick={() => void syncCatalog()} loading={syncing}>
                  Refresh
                </GlassButton>
                <button onClick={() => setShowAddModels(false)} className="text-white/40 hover:text-white/60"><X className="h-5 w-5" /></button>
              </div>
            </div>
            {addModelsPanel === 'loading' ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 text-sky-400 animate-spin mx-auto mb-3" />
                  <p className="text-sm text-white/50">Discovering models from all configured providers...</p>
                </div>
              </div>
            ) : addModelsPanel === 'error' ? (
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="max-w-lg rounded-xl border border-red-500/20 bg-red-500/5 px-5 py-4 text-center">
                  <p className="text-sm font-medium text-red-300 mb-1">Could not discover models</p>
                  <p className="text-sm text-red-300/70 mb-2 break-words">{discoverError}</p>
                  <p className="text-xs text-white/40">Click "Refresh" to try again.</p>
                </div>
              </div>
            ) : addModelsPanel === 'list' ? (
              <div className="flex-1 overflow-hidden flex flex-col">
                {discoverProblems.length > 0 && (
                  <div className="border-b border-white/10 bg-amber-500/5 px-5 py-2 text-xs text-amber-200/80">
                    Some providers did not answer, so their models are missing: {discoverProblems.join('; ')}
                  </div>
                )}
                {/* Filter bar */}
                <div className="border-b border-white/10 px-5 py-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/30" />
                      <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search models..."
                        className="w-full rounded-lg border border-white/10 bg-white/[0.03] pl-7 pr-3 py-1.5 text-sm text-white outline-none focus:border-sky-400/50" />
                    </div>
                    <button onClick={() => setCacheOnlyFilter((v) => !v)}
                      className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition ${cacheOnlyFilter ? 'border-amber-400/40 bg-amber-400/10 text-amber-300' : 'border-white/10 bg-white/[0.03] text-white/50 hover:text-white/70'}`}>
                      <Zap className="h-3 w-3" /> Cache only
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <select value={filterSourceProvider} onChange={(e) => setFilterSourceProvider(e.target.value)}
                      className="rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-xs text-white/70 outline-none focus:border-sky-400/50">
                      <option value="all">All Sources</option>
                      {sourceProviderOptions.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                    <select value={filterUpstreamProvider} onChange={(e) => setFilterUpstreamProvider(e.target.value)}
                      className="rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-xs text-white/70 outline-none focus:border-sky-400/50">
                      <option value="all">All Providers</option>
                      {upstreamProviderOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <input type="text" value={filterMaxCost} onChange={(e) => setFilterMaxCost(e.target.value)} placeholder="Max $/M in"
                      className="w-24 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-xs text-white/70 outline-none focus:border-sky-400/50 placeholder:text-white/30" />
                    <input type="text" value={filterMinCachePct} onChange={(e) => setFilterMinCachePct(e.target.value)} placeholder="Min saving %" title="Models whose cached input is at least this much cheaper than fresh input"
                      className="w-24 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-xs text-white/70 outline-none focus:border-sky-400/50 placeholder:text-white/30" />
                    <input type="text" value={filterMinContext} onChange={(e) => setFilterMinContext(e.target.value)} placeholder="Min ctx (K)"
                      className="w-24 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-xs text-white/70 outline-none focus:border-sky-400/50 placeholder:text-white/30" />
                    {(filterSourceProvider !== 'all' || filterUpstreamProvider !== 'all' || filterMaxCost || filterMinCachePct || filterMinContext || cacheOnlyFilter) && (
                      <button onClick={() => { setFilterSourceProvider('all'); setFilterUpstreamProvider('all'); setFilterMaxCost(''); setFilterMinCachePct(''); setFilterMinContext(''); setCacheOnlyFilter(false); }}
                        className="text-xs text-white/40 hover:text-white/60">Clear filters</button>
                    )}
                    <span className="ml-auto text-xs text-white/40">{filteredDiscovered.length} of {discovered.length} models</span>
                  </div>
                </div>
                {/* Model table */}
                <div className="flex-1 overflow-y-auto p-5">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-white/40">
                      <th className="pb-2 pr-4">Model</th><th className="pb-2 pr-4">Source</th><th className="pb-2 pr-4">Provider</th>
                      <th className="pb-2 pr-4 text-center" title={CACHE_HEADER_HINT}>Cache saving</th><th className="pb-2 pr-4 text-right">Cost (in/out per M)</th>
                      <th className="pb-2 pr-4">Context</th><th className="pb-2"></th>
                    </tr></thead>
                    <tbody>
                      {filteredDiscovered.slice(0, 300).map((model) => {
                        const inCatalog = catalogIds.has(model.upstreamModelKey) || catalogIds.has(model.id);
                        return (
                          <tr key={model.id} className="border-b border-white/5">
                            <td className="py-2 pr-4"><div className="font-medium text-white/85">{model.displayName}</div><div className="text-xs text-white/40">{model.upstreamModelKey}</div></td>
                            <td className="py-2 pr-4">
                              <div className="flex items-center gap-1.5 text-white/60">
                                <ProviderIcon providerId={model.sourceProviderId} className="h-3.5 w-3.5 shrink-0" />
                                <span>{model.sourceProviderId ?? <span className="text-white/25">—</span>}</span>
                              </div>
                            </td>
                            <td className="py-2 pr-4">
                              <div className="flex items-center gap-1.5 text-white/60">
                                <ProviderIcon providerId={model.upstreamProvider} className="h-3.5 w-3.5 shrink-0" />
                                <span>{model.upstreamProvider ?? <span className="text-white/25">—</span>}</span>
                              </div>
                            </td>
                            <td className="py-2 pr-4 text-center"><CacheSavingChip model={model} /></td>
                            <td className="py-2 pr-4 text-right text-white/60">{formatPrice(model.upstreamInputCostPerMillion)} / {formatPrice(model.upstreamOutputCostPerMillion)}</td>
                            <td className="py-2 pr-4 text-white/60">{model.contextWindow ? `${(model.contextWindow / 1000).toFixed(0)}K` : '—'}</td>
                            <td className="py-2">{inCatalog ? <Badge variant="success">Added</Badge> : <button onClick={() => void addModel(model)} className="text-emerald-400 hover:text-emerald-300"><Plus className="h-4 w-4" /></button>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="max-w-lg text-center">
                  <p className="text-sm text-white/40 mb-2">No models discovered. Click "Refresh" to try again.</p>
                  {discoverProblems.length > 0 && (
                    <p className="text-xs text-amber-200/80 mb-2 break-words">Did not answer: {discoverProblems.join('; ')}</p>
                  )}
                  {discoverSkipped.length > 0 && (
                    <p className="text-xs text-white/40 mb-2 break-words">Not asked: {discoverSkipped.join(', ')}</p>
                  )}
                  <p className="text-xs text-white/30">Make sure you have at least one source provider with an API key configured.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Source Provider panel */}
      {slidePanel === 'sourceProvider' && (
        <SourceProviderPanel
          providers={sourceProviders}
          onClose={() => setSlidePanel(null)}
          onSaved={() => { void loadSourceProviders(); }}
        />
      )}

      {/* Pricing side panel */}
      {slidePanel === 'pricing' && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSlidePanel(null)} />
          <div className="relative w-full max-w-md h-full bg-[rgba(20,20,35,0.98)] border-l border-white/10 overflow-y-auto shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[rgba(20,20,35,0.98)] px-5 py-4">
              <h3 className="text-sm font-semibold text-white">Pricing & Margin</h3>
              <button onClick={() => setSlidePanel(null)} className="text-white/40 hover:text-white/60"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Default Markup %</span>
                <input type="number" value={form.defaultMarkupPercent}
                  onChange={(e) => setForm((c) => c ? { ...c, defaultMarkupPercent: Number(e.target.value) } : c)}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50" />
                <p className="text-xs text-white/40">Applied to all models with markup mode "Inherit default". The markup is applied to (model cost + guardrail cost).</p>
              </label>
              <GlassButton variant="primary" icon={<Save className="h-4 w-4" />} onClick={() => void saveSettings()} loading={saving} className="w-full">Save Settings</GlassButton>
              <GlassButton variant="secondary" icon={<RefreshCw className="h-4 w-4" />} onClick={() => void recomputePrices()} className="w-full">
                Recompute Sale Prices
              </GlassButton>
              <p className="text-xs text-white/40">Recomputes sale prices from current markup, resolves guardrail pricing, and syncs the pricing table — all in one action.</p>
              {/* AD-20260714-07: Update all pricing from providers */}
              <GlassButton variant="primary" icon={<RefreshCw className="h-4 w-4" />} onClick={() => void updateAllPricingFromProviders()} loading={syncing} className="w-full">
                Update All Pricing from Providers
              </GlassButton>
              <p className="text-xs text-white/40">Fetches the latest upstream costs from OpenRouter and all configured providers, discovers new models, recomputes sale prices, and syncs the pricing table.</p>
              <GlassButton variant="secondary" icon={<RefreshCw className="h-4 w-4" />} onClick={() => void syncPricingTable()} className="w-full">
                Sync Pricing Table
              </GlassButton>
              <p className="text-xs text-white/40">Pushes the catalog's sale prices into the metered pricing table. Exposed models are added or updated; removed models with no activity are pruned.</p>
              <GlassButton variant="secondary" icon={<Shield className="h-4 w-4" />} onClick={() => void resolveGuardrailPricing()} className="w-full">
                Resolve Guardrail Pricing
              </GlassButton>
              <p className="text-xs text-white/40">Looks up the guardrail model's upstream cost, applies it to every guardrail-enabled model, and recomputes sale prices.</p>
            </div>
          </div>
        </div>
      )}

      {/* Gateway Controls side panel */}
      {slidePanel === 'gatewayControls' && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSlidePanel(null)} />
          <div className="relative w-full max-w-md h-full bg-[rgba(20,20,35,0.98)] border-l border-white/10 overflow-y-auto shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[rgba(20,20,35,0.98)] px-5 py-4">
              <h3 className="text-sm font-semibold text-white">Gateway Controls</h3>
              <button onClick={() => setSlidePanel(null)} className="text-white/40 hover:text-white/60"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <label className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <div><p className="text-sm font-semibold text-white/85">Enable Gateway</p><p className="mt-1 text-xs text-white/45">Master toggle for the managed provider.</p></div>
                <input type="checkbox" className="h-4 w-4 rounded border-white/20 bg-white/10 text-emerald-400" checked={form.enabled}
                  onChange={(e) => setForm((c) => c ? { ...c, enabled: e.target.checked } : c)} />
              </label>
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Rollout Mode</span>
                <select value={form.rolloutMode}
                  onChange={(e) => setForm((c) => c ? { ...c, rolloutMode: e.target.value as ModelGatewaySettings['rolloutMode'] } : c)}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50">
                  <option value="disabled">Disabled</option><option value="pilot">Pilot (allowlist)</option><option value="all-orgs">All organizations</option>
                </select>
              </label>
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Upstream Timeout (ms)</span>
                <input type="number" step="1000" value={form.upstreamTimeoutMs}
                  onChange={(e) => setForm((c) => c ? { ...c, upstreamTimeoutMs: Number(e.target.value) } : c)}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50" />
              </label>
              <GlassButton variant="primary" icon={<Save className="h-4 w-4" />} onClick={() => void saveSettings()} loading={saving} className="w-full">Save Settings</GlassButton>
              <GlassButton variant="secondary" icon={<Zap className="h-4 w-4" />} onClick={() => void testConnection()} loading={testing} className="w-full">
                Test OpenRouter Connection
              </GlassButton>
              <p className="text-xs text-white/40">Calls OpenRouter with the stored key and reports how many models it can see.</p>
              {/* Allowlist / Blocklist */}
              <div className="space-y-2 pt-2">
                <button onClick={() => setSlidePanel('whitelist')} className="flex items-center justify-between w-full rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 hover:bg-white/[0.05] transition">
                  <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-emerald-300" /><span className="text-sm text-white/85">Allowlist</span></div>
                  <Badge variant="default">{form.pilotOrgIds.length}</Badge>
                </button>
                <button onClick={() => setSlidePanel('blacklist')} className="flex items-center justify-between w-full rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 hover:bg-white/[0.05] transition">
                  <div className="flex items-center gap-2"><Ban className="h-4 w-4 text-red-300" /><span className="text-sm text-white/85">Blocklist</span></div>
                  <Badge variant="default">{form.blockedOrgIds.length}</Badge>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Model edit panel */}
      {editingModel && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setEditingModel(null)} />
          <div className="relative w-full max-w-md h-full bg-[rgba(20,20,35,0.98)] border-l border-white/10 overflow-y-auto shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[rgba(20,20,35,0.98)] px-5 py-4">
              <h3 className="text-sm font-semibold text-white">Edit Model Pricing</h3>
              <button onClick={() => setEditingModel(null)} className="text-white/40 hover:text-white/60">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Model info */}
              <div className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
                <p className="text-sm font-medium text-white/85">{editingModel.displayName}</p>
                <p className="text-xs text-white/40">{editingModel.id}</p>
                <p className="text-xs text-white/40 mt-1">{editingModel.upstreamProvider}</p>
              </div>

              {/* Display name */}
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Display Name</span>
                <input
                  type="text"
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                />
              </label>

              {/* Cost breakdown */}
              <div className="rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3 space-y-1">
                <p className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Cost Breakdown (per 1M tokens)</p>
                <div className="flex justify-between text-xs">
                  <span className="text-white/50">Model input:</span>
                  <span className="text-white/70">{formatPrice(editingModel.upstreamInputCostPerMillion)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-white/50">Model output:</span>
                  <span className="text-white/70">{formatPrice(editingModel.upstreamOutputCostPerMillion)}</span>
                </div>
                {editingModel.supportsCaching && (
                  <>
                    <div className="flex justify-between text-xs">
                      <span className="text-white/50">Cache read:</span>
                      <span className="text-amber-300">{formatPrice(editingModel.upstreamCachedInputCostPerMillion)}/M{cacheSavingOf(editingModel) ? ` (${cacheSavingOf(editingModel)!.percent}% cheaper than fresh input)` : ''}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-white/50">Cache write:</span>
                      <span className="text-white/70">{formatPrice(editingModel.upstreamCacheWriteCostPerMillion)}/M</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-xs">
                  <span className="text-white/50">Guardrail (input):</span>
                  <span className="text-white/70">{editGuardrailEnabled ? formatPrice(editingModel.guardrailCostPerMillion) : 'disabled'}</span>
                </div>
                <div className="border-t border-white/10 mt-2 pt-2 flex justify-between text-xs">
                  <span className="text-white/50 font-semibold">Effective input cost:</span>
                  <span className="text-emerald-300 font-semibold">{formatPrice(editingModel.upstreamInputCostPerMillion + (editGuardrailEnabled ? editingModel.guardrailCostPerMillion : 0))}</span>
                </div>
              </div>

              {/* Markup mode */}
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Markup Mode</span>
                <select
                  value={editMarkupMode}
                  onChange={(e) => setEditMarkupMode(e.target.value as 'inherit-default' | 'override-percent' | 'fixed-sell-price')}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                >
                  <option value="inherit-default">Inherit default ({form?.defaultMarkupPercent ?? 30}%)</option>
                  <option value="override-percent">Override percentage</option>
                  <option value="fixed-sell-price">Fixed sell price</option>
                </select>
              </label>

              {/* Override percentage */}
              {editMarkupMode === 'override-percent' && (
                <label className="space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Markup Percentage</span>
                  <input
                    type="number"
                    step="0.1"
                    value={editMarkupPercent}
                    onChange={(e) => setEditMarkupPercent(Number(e.target.value))}
                    className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                  />
                  <p className="text-xs text-white/40">
                    Applied to (model cost + guardrail cost). Sale price = effective cost × (1 + {editMarkupPercent}%).
                    Preview: {formatPrice((editingModel.upstreamInputCostPerMillion + (editGuardrailEnabled ? editingModel.guardrailCostPerMillion : 0)) * (1 + editMarkupPercent / 100))}/M in,
                    {' '}{formatPrice(editingModel.upstreamOutputCostPerMillion * (1 + editMarkupPercent / 100))}/M out
                  </p>
                </label>
              )}

              {/* Fixed sell price */}
              {editMarkupMode === 'fixed-sell-price' && (
                <div className="space-y-3">
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Sale Input Price (per 1M tokens)</span>
                    <input
                      type="number"
                      step="0.01"
                      value={editSaleInput}
                      onChange={(e) => setEditSaleInput(Number(e.target.value))}
                      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                    />
                  </label>
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Sale Output Price (per 1M tokens)</span>
                    <input
                      type="number"
                      step="0.01"
                      value={editSaleOutput}
                      onChange={(e) => setEditSaleOutput(Number(e.target.value))}
                      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                    />
                  </label>
                  <p className="text-xs text-white/40">
                    Set the exact price you charge the customer. The markup percentage is derived from this.
                    Effective margin: {computeMargin(editingModel.upstreamInputCostPerMillion + (editGuardrailEnabled ? editingModel.guardrailCostPerMillion : 0), editSaleInput)}
                  </p>
                </div>
              )}

              {/* Guardrail toggle */}
              <label className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-white/85">Llama Guard screening</p>
                  <p className="mt-1 text-xs text-white/45">When enabled, the guardrail cost is added to this model's cost before markup.</p>
                </div>
                <input type="checkbox" className="h-4 w-4 rounded border-white/20 bg-white/10 text-emerald-400"
                  checked={editGuardrailEnabled}
                  onChange={(e) => setEditGuardrailEnabled(e.target.checked)} />
              </label>

              {/* Reasoning effort */}
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Reasoning Effort</span>
                <select
                  value={editReasoningEffort}
                  onChange={(e) => setEditReasoningEffort(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50"
                >
                  <option value="">Default (no override)</option>
                  <option value="none">None — no reasoning tokens</option>
                  <option value="minimal">Minimal — fastest, least reasoning</option>
                  <option value="low">Low — quick reasoning</option>
                  <option value="medium">Medium — balanced (recommended for most tasks)</option>
                  <option value="high">High — thorough reasoning</option>
                  <option value="xhigh">XHigh — maximum reasoning (slowest, most tokens)</option>
                </select>
                <p className="text-xs text-white/40">
                  Controls how much the model "thinks" before responding. Lower = faster and cheaper.
                  Higher = better reasoning but more tokens and slower. Only applies to models that support
                  reasoning/thinking tokens (e.g., DeepSeek, Claude, Qwen). Ignored by models that don't support it.
                </p>
              </label>

              {/* Exposed toggle */}
              <label className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-white/85">Exposed to users</p>
                  <p className="mt-1 text-xs text-white/45">When enabled, this model appears in the ANDL extension for enabled orgs.</p>
                </div>
                <input type="checkbox" className="h-4 w-4 rounded border-white/20 bg-white/10 text-emerald-400"
                  checked={editExposed}
                  onChange={(e) => setEditExposed(e.target.checked)} />
              </label>

              {/* Visible by default toggle */}
              <label className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-white/85">Visible by default</p>
                  <p className="mt-1 text-xs text-white/45">When enabled, this model is pre-selected for new users.</p>
                </div>
                <input type="checkbox" className="h-4 w-4 rounded border-white/20 bg-white/10 text-emerald-400"
                  checked={editVisibleByDefault}
                  onChange={(e) => setEditVisibleByDefault(e.target.checked)} />
              </label>

              {/* === Cache Strategy & Parameter Compatibility === */}
              <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-sky-300" />
                  <p className="text-xs font-semibold uppercase tracking-wider text-white/50">Cache Strategy & Parameter Compatibility</p>
                </div>
                <p className="text-xs text-white/40">
                  The gateway self-heals by learning which caching strategy and parameter workarounds work for each model.
                  {cacheStrategy ? ' Learned settings are shown below.' : ' No learned settings — the server uses defaults.'}
                </p>

                {loadingStrategy && (
                  <div className="flex items-center gap-2 text-xs text-white/40">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading...
                  </div>
                )}

                {/* Cache strategy */}
                <div className="space-y-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-white/60">Cache Strategy</span>
                    {cacheStrategy && <Badge>{cacheStrategy.strategy}</Badge>}
                  </div>

                  {/* Strategy dropdown */}
                  <label className="space-y-1.5">
                    <span className="text-xs text-white/40">Override strategy (manual control)</span>
                    <select
                      value={editCacheStrategy}
                      onChange={(e) => setEditCacheStrategy(e.target.value)}
                      className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-xs text-white outline-none focus:border-sky-400/50"
                    >
                      <option value="default">Default (auto-detect by provider)</option>
                      <option value="automatic">Automatic (provider-native caching)</option>
                      <option value="explicit">Explicit (per-block breakpoints)</option>
                      <option value="none">None (no caching)</option>
                    </select>
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => editingModel && void saveCacheStrategy(editingModel.upstreamModelKey, editCacheStrategy)}
                      disabled={savingStrategy}
                      className="flex items-center gap-1.5 rounded-lg border border-sky-400/20 bg-sky-400/10 px-2.5 py-1 text-xs text-sky-300 hover:bg-sky-400/20 transition disabled:opacity-50"
                    >
                      {savingStrategy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save strategy
                    </button>
                    {cacheStrategy && (
                      <button
                        onClick={() => editingModel && void saveStrategyOverride(editingModel.upstreamModelKey)}
                        className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1 text-xs text-white/60 hover:bg-white/[0.06] hover:text-white/80 transition"
                      >
                        <RefreshCw className="h-3 w-3" /> Reset to default
                      </button>
                    )}
                  </div>

                  {/* Stats */}
                  {cacheStrategy && (
                    <div className="grid grid-cols-3 gap-2 text-xs pt-1">
                      <div>
                        <p className="text-white/40">Requests</p>
                        <p className="text-white/70">{cacheStrategy.totalRequests}</p>
                      </div>
                      <div>
                        <p className="text-white/40">Cache hits</p>
                        <p className="text-emerald-300">{cacheStrategy.totalCacheHits}</p>
                      </div>
                      <div>
                        <p className="text-white/40">Cache writes</p>
                        <p className="text-amber-300">{cacheStrategy.totalCacheWrites}</p>
                      </div>
                    </div>
                  )}
                  {cacheStrategy?.lastError && (
                    <div className="rounded border border-red-500/20 bg-red-500/5 px-2 py-1.5">
                      <p className="text-xs text-red-300/70">Last error: {cacheStrategy.lastError.slice(0, 100)}</p>
                      <p className="text-xs text-white/30">{formatTimestamp(cacheStrategy.lastErrorAt)}</p>
                    </div>
                  )}
                  {cacheStrategy?.lastSuccessAt && (
                    <p className="text-xs text-white/30">Last success: {formatTimestamp(cacheStrategy.lastSuccessAt)}</p>
                  )}
                </div>

                {/* Parameter compatibility rules */}
                {paramCompatRules.length > 0 && (
                  <div className="space-y-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
                    <span className="text-xs font-semibold text-white/60">Parameter Workarounds</span>
                    {paramCompatRules.map((rule) => (
                      <div key={rule.id} className="text-xs space-y-0.5">
                        <div className="flex items-center gap-2">
                          <span className="text-white/70 font-mono">{rule.paramName}</span>
                          {rule.badValue && rule.badValue !== '*' && <span className="text-red-300/60">← {rule.badValue}</span>}
                          <span className="text-white/30">→</span>
                          {rule.action === 'remove' ? (
                            <span className="text-red-300/60">removed</span>
                          ) : (
                            <span className="text-emerald-300/60">{rule.workaroundValue}</span>
                          )}
                        </div>
                        <p className="text-white/30">Hits: {rule.totalHits} · {rule.lastErrorAt ? `Last error: ${formatTimestamp(rule.lastErrorAt)}` : 'No errors'}</p>
                      </div>
                    ))}
                    <button
                      onClick={() => editingModel && void resetParamCompat(editingModel.upstreamModelKey)}
                      className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1 text-xs text-white/60 hover:bg-white/[0.06] hover:text-white/80 transition"
                    >
                      <Trash2 className="h-3 w-3" /> Reset param compat
                    </button>
                  </div>
                )}
              </div>

              {/* Save button */}
              <GlassButton variant="primary" onClick={() => void saveModelEdit()} loading={savingModel} className="w-full">
                Save Changes
              </GlassButton>
            </div>
          </div>
        </div>
      )}

      {/* Whitelist / Blacklist management panel */}
      {slidePanel && slidePanel !== 'orgPicker' && slidePanel !== 'sourceProvider' && slidePanel !== 'pricing' && slidePanel !== 'gatewayControls' && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSlidePanel(null)} />
          <div className="relative w-full max-w-md h-full bg-[rgba(20,20,35,0.98)] border-l border-white/10 overflow-y-auto shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[rgba(20,20,35,0.98)] px-5 py-4">
              <div className="flex items-center gap-2">
                {slidePanel === 'whitelist' ? (
                  <Shield className="h-4 w-4 text-emerald-300" />
                ) : (
                  <Ban className="h-4 w-4 text-red-300" />
                )}
                <h3 className="text-sm font-semibold text-white">
                  {slidePanel === 'whitelist' ? 'Allowlist' : 'Blocklist'}
                </h3>
                <Badge variant="default">
                  {slidePanel === 'whitelist' ? form.pilotOrgIds.length : form.blockedOrgIds.length} orgs
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <GlassButton
                  variant="primary"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => openOrgPicker(slidePanel === 'whitelist' ? 'whitelist' : 'blacklist')}
                >
                  Add Orgs
                </GlassButton>
                <button onClick={() => setSlidePanel(null)} className="text-white/40 hover:text-white/60">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-2">
              <p className="text-xs text-white/40 mb-4">
                {slidePanel === 'whitelist'
                  ? 'Organizations in this list are explicitly allowed to use the managed gateway. Active when rollout mode is "Pilot".'
                  : 'Organizations in this list are always blocked from the managed gateway, regardless of rollout mode. Takes precedence over the allowlist.'}
              </p>

              {(slidePanel === 'whitelist' ? form.pilotOrgIds : form.blockedOrgIds).length === 0 ? (
                <div className="rounded-lg border border-dashed border-white/10 px-4 py-12 text-center text-sm text-white/30">
                  No organizations in this list yet. Click "Add Orgs" to search and add.
                </div>
              ) : (
                (slidePanel === 'whitelist' ? form.pilotOrgIds : form.blockedOrgIds).map((orgId) => (
                  <div key={orgId} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white/85 truncate">
                        {orgNamesCache[orgId]?.name ?? 'Loading...'}
                      </p>
                      <p className="text-xs text-white/40 truncate">
                        {orgNamesCache[orgId]?.slug || orgId}
                      </p>
                    </div>
                    <button
                      onClick={() => removeOrgFromList(orgId, slidePanel === 'whitelist' ? 'whitelist' : 'blacklist')}
                      className="ml-3 shrink-0 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-1.5 text-xs text-red-300 hover:bg-red-500/20 transition"
                    >
                      Remove
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Org picker panel (second-level slide-in) */}
      {slidePanel === 'orgPicker' && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSlidePanel(slideContext)} />
          <div className="relative w-full max-w-md h-full bg-[rgba(20,20,35,0.98)] border-l border-white/10 overflow-y-auto shadow-2xl">
            <div className="sticky top-0 z-10 border-b border-white/10 bg-[rgba(20,20,35,0.98)] px-5 py-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSlidePanel(slideContext)}
                    className="text-white/40 hover:text-white/60 flex items-center gap-1 text-sm"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to {slideContext === 'whitelist' ? 'Allowlist' : 'Blocklist'}
                  </button>
                </div>
                <button onClick={() => setSlidePanel(slideContext)} className="text-white/40 hover:text-white/60">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <h3 className="text-sm font-semibold text-white">
                Add organizations to {slideContext === 'whitelist' ? 'allowlist' : 'blocklist'}
              </h3>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
                <input
                  type="text"
                  value={orgSearchQuery}
                  onChange={(e) => {
                    setOrgSearchQuery(e.target.value);
                    void searchOrgs(e.target.value);
                  }}
                  autoFocus
                  placeholder="Search by org name or slug..."
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] pl-10 pr-3 py-2.5 text-sm text-white outline-none focus:border-sky-400/50"
                />
                {orgSearching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-white/30" />}
              </div>
            </div>

            <div className="p-5 space-y-2">
              {!orgSearchQuery.trim() ? (
                <div className="rounded-lg border border-dashed border-white/10 px-4 py-12 text-center text-sm text-white/30">
                  Start typing to search for organizations by name or slug.
                </div>
              ) : orgSearchResults.length === 0 && !orgSearching ? (
                <div className="rounded-lg border border-dashed border-white/10 px-4 py-12 text-center text-sm text-white/30">
                  No organizations found matching "{orgSearchQuery}".
                </div>
              ) : (
                orgSearchResults.map((org) => {
                  const currentList = slideContext === 'whitelist' ? form.pilotOrgIds : form.blockedOrgIds;
                  const isInList = currentList.includes(org.orgId);
                  const isInOtherList = slideContext === 'whitelist'
                    ? form.blockedOrgIds.includes(org.orgId)
                    : form.pilotOrgIds.includes(org.orgId);
                  return (
                    <div
                      key={org.orgId}
                      className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white/85 truncate">{org.name}</p>
                        <p className="text-xs text-white/40 truncate">
                          {org.slug} · {org.memberCount} member{org.memberCount === 1 ? '' : 's'}
                        </p>
                      </div>
                      <div className="ml-3 shrink-0">
                        {isInList ? (
                          <Badge variant="success">Added</Badge>
                        ) : isInOtherList ? (
                          <Badge variant="warning">
                            In {slideContext === 'whitelist' ? 'blocklist' : 'allowlist'}
                          </Badge>
                        ) : (
                          <button
                            onClick={() => addOrgToList(org.orgId)}
                            className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-300 hover:bg-emerald-500/20 transition flex items-center gap-1"
                          >
                            <Plus className="h-3 w-3" />
                            Add
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Save-before-action modal */}
      <ConfirmModal
        open={savePrompt !== null}
        title="Unsaved Changes"
        message={savePrompt ? `You have unsaved changes to the gateway settings. ${savePrompt.actionLabel} uses the saved values, not your current edits.\n\nSave now and continue?` : ''}
        confirmLabel="Save & Continue"
        cancelLabel="Cancel"
        loading={savePromptSaving}
        onConfirm={() => void handleSavePromptConfirm()}
        onCancel={handleSavePromptCancel}
      />
    </div>
  );
}
